import { useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, Menu, Users, X } from "lucide-react";

// LŪMEN-style navigation: wordmark left, numbered links, status strip right,
// animated hamburger + full-screen mobile menu overlay.
// Single piece of state: menuOpen.

const LINKS = [
  { number: "01.", label: "OVERVIEW", href: "/overview", delay: 350 },
  { number: "02.", label: "VOTING", href: "/voting", delay: 450 },
  { number: "03.", label: "BUDGET", href: "/budget", delay: 550 },
  { number: "04.", label: "FAQS", href: "/faq", delay: 650 },
];

function NavItem({
  number,
  label,
  href,
  delay,
}: {
  number: string;
  label: string;
  href: string;
  delay: number;
}) {
  return (
    <Link to={href} className="flex items-center gap-[3px] anim-fade-up" style={{ animationDelay: `${delay}ms` }}>
      <span className="font-manrope text-[13px] leading-[15.6px] text-[#AFDDFF]/80">{number}</span>
      <span className="cursor-pointer font-manrope text-[13px] leading-[15.6px] text-white transition-colors hover:text-[#AFDDFF]">
        {label}
      </span>
    </Link>
  );
}

export default function Navigation({
  wordmark,
  expectedAttendees,
  totalCohort,
  status,
  onSignOut,
}: {
  wordmark: string;
  expectedAttendees: number;
  totalCohort: number;
  status: string;
  onSignOut: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const chip = status.toUpperCase().replace(/_/g, " ");

  return (
    <>
      <nav className="absolute left-0 top-0 z-50 flex w-full items-center gap-4 px-5 py-5 md:px-[35px] md:py-[24px]">
        <div className="flex items-center gap-6 lg:gap-9">
          <Link to="/" className="anim-fade-up" style={{ animationDelay: "200ms" }}>
            <span className="font-graphik whitespace-nowrap text-[18px] leading-[21px] text-white md:text-[21px]">
              {wordmark}
            </span>
          </Link>

          {/* Inline nav only when there is real room for it (lg+, content-driven).
              Between lg and xl the status strip collapses; below lg it's the menu. */}
          <div className="hidden items-center gap-6 lg:flex xl:gap-9">
            {LINKS.map((l) => (
              <NavItem key={l.href} {...l} />
            ))}
            <button
              type="button"
              onClick={onSignOut}
              className="flex items-center gap-2 font-manrope text-[13px] leading-[15.6px] text-white transition-colors hover:text-[#AFDDFF]"
            >
              <LogOut className="h-4 w-4 text-[#AFDDFF]/80" strokeWidth={1.5} aria-hidden="true" />
              SIGN OUT
            </button>
          </div>
        </div>

        {/* Status strip — secondary metadata, only shown when the header has room */}
        <div
          className="ml-auto hidden items-center gap-[12px] xl:flex anim-slide-right"
          style={{ animationDelay: "600ms" }}
        >
          <Users className="h-[15px] w-[15px]" strokeWidth={1.5} aria-hidden="true" />
          <span className="font-manrope text-[13px] leading-[15.6px] text-white">
            ~{expectedAttendees} EXPECTED
          </span>
          <span className="font-manrope text-[13px] leading-[15.6px] text-[#AFDDFF]">
            [ COHORT : {totalCohort} ]
          </span>
          <span className="ml-[20px] font-manrope text-[13px] leading-[15.6px] text-white">STATUS:</span>
          <span className="rounded-[3px] bg-[#AFDDFF] px-[5px] py-[2px] font-manrope text-[13px] leading-[15.6px] text-black">
            {chip}
          </span>
        </div>

        {/* Hamburger (mobile) */}
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
          className="relative ml-auto flex h-[40px] w-[40px] items-center justify-center anim-fade-in lg:hidden"
          style={{ animationDelay: "400ms" }}
        >
          <span
            className={`absolute transition-all duration-300 ease-[cubic-bezier(0.76,0,0.24,1)] ${
              menuOpen ? "rotate-90 scale-50 opacity-0" : "rotate-0 scale-100 opacity-100"
            }`}
          >
            <Menu className="h-[22px] w-[22px] text-white" strokeWidth={1.5} />
          </span>
          <span
            className={`absolute transition-all duration-300 ease-[cubic-bezier(0.76,0,0.24,1)] ${
              menuOpen ? "rotate-0 scale-100 opacity-100" : "-rotate-90 scale-50 opacity-0"
            }`}
          >
            <X className="h-[22px] w-[22px] text-white" strokeWidth={1.5} />
          </span>
        </button>
      </nav>

      {/* Mobile menu overlay */}
      <div
        className={`fixed inset-0 z-50 transition-all duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] lg:hidden ${
          menuOpen ? "visible" : "invisible"
        }`}
      >
        <div
          onClick={() => setMenuOpen(false)}
          className={`absolute inset-0 bg-black/90 backdrop-blur-md transition-opacity duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] ${
            menuOpen ? "opacity-100" : "opacity-0"
          }`}
        />
        <div
          className={`relative flex h-full flex-col px-5 pb-10 pt-24 transition-all duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] ${
            menuOpen ? "translate-y-0 opacity-100" : "-translate-y-4 opacity-0"
          }`}
        >
          <button
            type="button"
            onClick={() => setMenuOpen(false)}
            aria-label="Close menu"
            className="absolute right-5 top-5 flex h-[40px] w-[40px] items-center justify-center"
          >
            <X className="h-[22px] w-[22px] text-white" strokeWidth={1.5} />
          </button>

          <nav className="flex flex-col gap-8">
            {LINKS.map((l, i) => (
              <Link
                key={l.href}
                to={l.href}
                onClick={() => setMenuOpen(false)}
                style={{ transitionDelay: menuOpen ? `${150 + i * 75}ms` : "0ms" }}
                className={`flex items-center gap-3 transition-all duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] ${
                  menuOpen ? "translate-x-0 opacity-100" : "-translate-x-6 opacity-0"
                }`}
              >
                <span className="font-manrope text-[14px] leading-[1] text-[#AFDDFF]/80">{l.number}</span>
                <span className="font-manrope text-[28px] leading-[1.2] tracking-tight text-white">
                  {l.label}
                </span>
              </Link>
            ))}
            <button
              type="button"
              onClick={() => { setMenuOpen(false); onSignOut(); }}
              className="mt-2 flex min-h-12 items-center gap-3 border-t border-white/10 pt-6 text-left font-manrope text-[28px] leading-[1.2] tracking-tight text-white transition-colors hover:text-[#AFDDFF]"
            >
              <LogOut className="h-6 w-6 text-[#AFDDFF]/80" strokeWidth={1.5} aria-hidden="true" />
              <span>SIGN OUT</span>
            </button>
          </nav>

          {/* Event status block, pinned to the bottom */}
          <div
            style={{ transitionDelay: menuOpen ? "450ms" : "0ms" }}
            className={`mt-auto border-t border-white/10 pt-10 transition-all duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] ${
              menuOpen ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
            }`}
          >
            <div className="mb-3 flex items-center gap-[10px]">
              <Users className="h-[15px] w-[15px]" strokeWidth={1.5} aria-hidden="true" />
              <span className="font-manrope text-[13px] leading-[15.6px] text-white">
                ~{expectedAttendees} EXPECTED
              </span>
              <span className="font-manrope text-[13px] leading-[15.6px] text-[#AFDDFF]">
                [ COHORT : {totalCohort} ]
              </span>
            </div>
            <div className="flex items-center gap-[8px]">
              <span className="font-manrope text-[13px] leading-[15.6px] text-white">STATUS:</span>
              <span className="rounded-[3px] bg-[#AFDDFF] px-[5px] py-[2px] font-manrope text-[13px] leading-[15.6px] text-black">
                {chip}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
